import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Sun, Moon, X } from 'lucide-react';
import { useThemeStore } from '@/store/theme.store.ts';

interface ThemePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThemePanel: React.FC<ThemePanelProps> = ({ isOpen, onClose }) => {
  const { theme, setTheme } = useThemeStore();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[55]"
            onClick={onClose}
          />

          <motion.aside
            initial={{ opacity: 0, x: 360 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 360 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-80 bg-[var(--bg-card)] border-l border-[var(--border-light)] shadow-2xl z-[61]"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-light)]">
              <div className="flex items-center gap-2">
                <Palette size={18} className="text-blue-600" />
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">主题设置</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">选择博客外观模式。</p>

              <button
                type="button"
                onClick={() => setTheme('light')}
                className={`w-full px-4 py-3 border flex items-center justify-between transition-colors ${
                  theme === 'light'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/20'
                    : 'border-[var(--border-light)] text-[var(--text-primary)] hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                }`}
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <Sun size={16} />
                  浅色模式
                </span>
                {theme === 'light' ? <span className="text-xs">已启用</span> : null}
              </button>

              <button
                type="button"
                onClick={() => setTheme('dark')}
                className={`w-full px-4 py-3 border flex items-center justify-between transition-colors ${
                  theme === 'dark'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/20'
                    : 'border-[var(--border-light)] text-[var(--text-primary)] hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                }`}
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <Moon size={16} />
                  深色模式
                </span>
                {theme === 'dark' ? <span className="text-xs">已启用</span> : null}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default ThemePanel;
